/**
 * The two partner identity colours — the GELS.
 *
 * One couple, two people, and a dozen places that have to say which of them a thing belongs to —
 * a dot beside a Sync answer, a bar on the Scoreboard, an avatar, the fill behind an answer
 * bubble. Those all have to agree, so the pair is decided once, here, rather than at each call
 * site. IDENTITY, NEVER SEMANTICS: nothing may be read off a gel — a partner is never "the good
 * one" or "the warning one", and both concept sheets say so in as many words.
 *
 * WHY THIS IS JS AND NOT A CSS TOKEN, which is the one thing worth knowing before editing it.
 * Every other colour in the restyle is a `--var` in `global.css`, resolved by the look on the
 * document root. A gel cannot be: it is chosen per ROW, off data, at render time — `partnerTint(
 * answer.by)` — and NativeWind cannot compose a class name out of a runtime value. So the pair
 * ships as literal hex handed to `style={{ backgroundColor }}`, and this file carries the same
 * four-way table `global.css` does: look × mode.
 *
 * A is rose-clay and B is midnight, in BOTH looks and BOTH modes, which is the sheets' own rule:
 * a person's colour changing when the lights go out is a different person. What DOES move is what
 * sits beside the fill — the type that has to stay readable on the ground, and the hairline that
 * keeps a midnight gel from vanishing on a midnight surface. B is the case that forces this: on a
 * dark card it measures 1.00:1, so it is never re-hued, it is given an EDGE.
 *
 * Contrast figures below are the sheets' own, measured rather than eyeballed.
 *
 * ONE PLACE TAKES A READING OF ITS OWN, and it is `board` — see the field. The Scoreboard draws
 * the same person as a numeral, an avatar, a bar and a swatch on ONE card, and picking `type` for
 * some of those and `fill` for the rest is how Gabi ended up being two colours at once in the
 * dark. `board` is the whole person, moved together; the roles above are unchanged and every other
 * screen still reads them.
 */
import { useStore, type Look } from '@/store';

/** Which half of the pairing a thing belongs to. The server's own vocabulary (`Me.partner`). */
export type PartnerId = 'A' | 'B';

export interface PartnerTint {
  /** The solid identity colour: a filled dot, a bar, an avatar ground. Never changes. */
  fill: string;
  /** Text and glyphs drawn ON `fill`. Measured at 4.5:1 or better against it. */
  ink: string;
  /** The gel as TYPE, on the card — the `who` caption over a bubble, a score numeral. */
  type: string;
  /** The gel's hairline: a bubble border, an avatar edge. What keeps B off a dark surface. */
  line: string;
  /** The gel as a small SHAPE — an 8px dot, a score-bar segment, a breakdown column marker. */
  mark: string;
  /** The gel at wash strength: a bubble ground. Opaque, because a native View wants a colour. */
  soft: string;
  /**
   * THE ONE COLOUR A PERSON IS DRAWN IN ON THE SCOREBOARD — every object, without exception.
   *
   * The Score card is the only place in the app that draws the same person four ways at once: a
   * 44px numeral, a 40px avatar tile, a share of the bar and an 8px breakdown swatch. It used to
   * take three different roles to do it — `type` for the numeral, `mark` for the swatch, `fill`
   * for the bar and the avatar — and in the dark rooms those are not one colour. On Bouquet at
   * night B's `type` is the lifted #C2B8C6 and B's `fill` is the raw midnight, so the legend and
   * the numeral read pale slate while the bar and the avatar stayed navy and nearly vanished: one
   * person, two colours, on one card, which is the identity rule broken by a card that has to
   * carry it hardest.
   *
   * So the board gets a reading of its own, and the rule behind the values is one sentence: IN A
   * LIT ROOM IT IS THE PERSON'S OWN SOLID GEL, AND IN A DARK ONE IT IS THAT GEL LIFTED OFF THE
   * CARD. B is what forces the second half — midnight measures 1.08:1 on Bouquet's night card and
   * 1.00:1 on Tuxedo's, and a hairline can rescue an 8px square or a 10px bar but there is no
   * edge you can put on a 44px numeral. Lifting is not re-hueing: it is the same midnight and the
   * same clay carried up through the room they are standing in, which is exactly what `type` and
   * `mark` already do at night. What changes is that the WHOLE person moves together.
   *
   * Measured, against each look's own card:
   *   bouquet light  A #C1806B 3.20 · B #1E2532 15.37     (3.20 is AA for a 44px display numeral)
   *   bouquet dark   A #D8B2A3 8.52 · B #C2B8C6  8.64
   *   tuxedo  light  A #C1806B 3.20 · B #1E2532 15.37
   *   tuxedo  dark   A #DAB7A9 8.29 · B #A7B1C9  7.15
   *
   * The existing `line` hairline is not retired by any of this and is still drawn where it always
   * was — Tuxedo rings B's avatar on its own band at 1.12, and the swatch keeps its edge. It is
   * belt-and-braces now rather than the only thing holding a shape together.
   */
  board: string;
  /**
   * What is drawn ON `board` — in practice the avatar's two initials, and nothing else yet.
   *
   * It is NOT always `ink`: `ink` is measured against `fill`, and in a dark room `board` is the
   * lifted gel, which is a PALE ground. So B's initials are cream on midnight by day and near-black
   * on lilac by night — the same person, still legible, because the ground under them moved.
   * Measured at 5.38:1 at worst (Tuxedo light A) and 9.64 at best.
   */
  boardInk: string;
}

/**
 * look × mode × partner. Sixteen entries, all four tables total, no fallthrough.
 *
 * Bouquet's gels sit on a blush room: A carries dark initials (white on it is only 3.20:1) and B
 * is nearly black by day, so B's own type IS its fill. At night both lift — A to a creamed clay
 * #D8B2A3 at 8.52 on the card, B to a warmed #C2B8C6 at 6.5 (see the note on that row).
 *
 * BOUQUET'S ROOM IS WARM, AND SO IS EVERY VALUE DRAWN IN IT. B's fill is a
 * midnight navy, and a navy mixed straight toward a white card lands on #E4E5E6 — a cold grey-blue
 * that reads as GREYED OUT in a blush room, so B's own answers were the dullest thing on B's own
 * page. At night the raw navy was worse: the only cold object in a maroon room, reading as a
 * system chip rather than as a person. Both are now the same navy blended toward the GROUND it
 * sits on — #EBE3DF against the white card, #2F1D22 against the maroon one — which keeps B's
 * bubble recognisably B's and stops it being the one thing on the page from another look.
 *
 * `soft` is the WASH ONLY. `fill`, `type`, `line` and `mark` are untouched: a person's colour
 * changing when the lights go out is a different person, and the identity is carried by those
 * four. Tuxedo is unaffected — its room is monochrome and both bubbles take `card-2` there by the
 * sheet's own decision, with the hairline and the caption carrying identity instead.
 *
 * Tuxedo's room is monochrome, so the gels are the only colour in it that is not the one red or
 * the one gold. Its bubble ground is `card-2` in both modes rather than a tint of the gel — the
 * sheet's decision, and it is why both partners' `soft` is the same grey there: what says whose
 * bubble it is, is the LINE and the `who` caption, not the fill.
 */
const TINTS: Record<Look, Record<'light' | 'dark', Record<PartnerId, PartnerTint>>> = {
  bouquet: {
    light: {
      // clay #C1806B: 3.20 on the white card, and it carries ink #1C1111 at 5.77.
      A: { fill: '#C1806B', ink: '#1C1111', type: '#95472F', line: '#C1806B', mark: '#C1806B', soft: '#F8F0ED', board: '#C1806B', boardInk: '#1C1111' },
      // midnight #1E2532: 15.37 on the card, cream ink at 13.35. By day it needs no edge.
      // `soft` is the WARM blend (see the note on Bouquet's washes below), not the cold #E4E5E6
      // that a straight midnight-toward-white mix lands on.
      B: { fill: '#1E2532', ink: '#F4EEE7', type: '#1E2532', line: '#1E2532', mark: '#1E2532', soft: '#EBE3DF', board: '#1E2532', boardInk: '#F4EEE7' },
    },
    dark: {
      A: { fill: '#C1806B', ink: '#1C1111', type: '#D8B2A3', line: '#C1806B', mark: '#C1806B', soft: '#5C2621', board: '#D8B2A3', boardInk: '#1C1111' },
      // 1.08 against the night card, which is the whole reason the gel has to LIFT to be seen at
      // all. It lifts through the room now, not through open sky: #C2B8C6 at 6.5 on the card
      // instead of the #A7B1C9 it used to take, which was the same midnight lit from a window and
      // the only cold object in a maroon room. Still one hue lifted, still never a second colour —
      // and `soft` is the same navy taken all the way down toward the ground it sits on.
      B: { fill: '#1E2532', ink: '#F4EEE7', type: '#C2B8C6', line: '#C2B8C6', mark: '#C2B8C6', soft: '#2F1D22', board: '#C2B8C6', boardInk: '#1C1111' },
    },
  },
  tuxedo: {
    light: {
      A: { fill: '#C1806B', ink: '#171B26', type: '#95472F', line: '#C1806B', mark: '#C1806B', soft: '#EFEFF0', board: '#C1806B', boardInk: '#171B26' },
      // #6B7078 rather than the gel itself: on the header band a midnight avatar reads 1.11, so
      // Tuxedo gives B an edge in BOTH modes and not only at night.
      B: { fill: '#1E2532', ink: '#FAFAFA', type: '#1E2532', line: '#6B7078', mark: '#1E2532', soft: '#EFEFF0', board: '#1E2532', boardInk: '#FAFAFA' },
    },
    dark: {
      A: { fill: '#C1806B', ink: '#171B26', type: '#DAB7A9', line: '#CB9684', mark: '#C1806B', soft: '#252D3C', board: '#DAB7A9', boardInk: '#171B26' },
      B: { fill: '#1E2532', ink: '#FAFAFA', type: '#A7B1C9', line: '#A7B1C9', mark: '#A7B1C9', soft: '#252D3C', board: '#A7B1C9', boardInk: '#171B26' },
    },
  },
};

/**
 * The identity colours for one partner, in the look and mode that are on RIGHT NOW.
 *
 * Read from the store at call time, deliberately: a module-scope copy would be the look the app
 * booted in, for ever, and the one place that shows up is the one place it matters — a Score card
 * that keeps yesterday's gels after somebody switches theme in Settings. Every component that
 * calls this already re-renders on a look or theme change, because it subscribes to `useStore`.
 */
export function partnerTint(p: PartnerId): PartnerTint {
  const { look, theme } = useStore.getState();
  return TINTS[look][theme][p];
}

/**
 * The other one.
 *
 * Worth a named function rather than a ternary at each site: "the partner" is read from the
 * VIEWER's seat everywhere in this app, and a hand-written `p === 'A' ? 'B' : 'A'` is the shape
 * that gets copied once with the arms the wrong way round.
 */
export function otherPartner(p: PartnerId): PartnerId {
  return p === 'A' ? 'B' : 'A';
}
